import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Form,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { formatDistance } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import {
  IconEye,
  IconSearch,
  IconFileDownload,
  IconSend,
  IconTrash,
} from "@tabler/icons-react";
import { AUTOMATIONS_URL } from "../../api/Urls";
import { api } from "../../api/Middleware";
import Swal from "sweetalert2";
import NotificationsContext from "../../contexts/NotificationsContext";
import DatatablePaginated from "../../jsx/components/DatatablePaginated";

const MessageHistory = ({ automationId }) => {
  const { setNotify } = useContext(NotificationsContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "all",
    dateRange: "7days",
    search: "",
    page: 1,
    page_size: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessageHistory();
  }, [automationId, filters]);

  const fetchMessageHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `${AUTOMATIONS_URL}${automationId}/messages/`,
        {
          params: { ...filters },
        }
      );
      setMessages(response.data.results || []);
      setTotalRows(response.data.count || 0);
    } catch (error) {
      console.error("Erro ao buscar histórico de mensagens:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
      page: 1, // Volta para a primeira página quando filtros são alterados
    });
  };

  const handlePageChange = (page) => {
    setFilters({
      ...filters,
      page: page,
    });
  };

  const getStatusBadge = (message) => {
    if (message.last_send) {
      return <Badge bg="success">Enviada</Badge>;
    } else if (message.status === "failed") {
      return <Badge bg="danger">Falha</Badge>;
    } else {
      return <Badge bg="warning">Pendente</Badge>;
    }
  };

  const getTemplateActionLabel = (action) => {
    const actions = {
      confirmation_message: "Confirmação",
      tips_message: "Dicas",
      feedback: "Feedback",
      reminder_message: "Lembrete",
      information: "Informação",
      generic: "Genérico",
    };
    return actions[action] || action;
  };

  const formatPhone = (phone) => {
    if (!phone) return "-";

    let formattedPhone = phone;

    if (phone.includes("+55")) {
      formattedPhone = phone.replace("+55", "");

      // Se não tiver 11 dígitos e começar com DDD, adiciona o 9 depois do DDD
      if (formattedPhone.length === 10) {
        formattedPhone =
          formattedPhone.slice(0, 2) + "9" + formattedPhone.slice(2);
      }

      return `(${formattedPhone.slice(0, 2)}) ${formattedPhone.slice(
        2,
        7
      )}-${formattedPhone.slice(7)}`;
    }

    return phone; // Retorna o número original se não for um número brasileiro
  };

  const exportToCSV = () => {
    // Implementação de exportação para CSV
    alert("Exportação para CSV será implementada");
  };

  const onSelectedRowsChange = (state) => {
    if (state.selectedRows && state.selectedRows.length > 0) {
      setSelectedRows(state.selectedRows);
      setShowDeleteButton(true);
    } else {
      setShowDeleteButton(false);
      setSelectedRows([]);
    }
  };

  const handleBulkDelete = () => {
    const ids = selectedRows.map((row) => row.id);
    if (ids.length === 0) {
      Swal.fire(
        "Oops!",
        "Selecione pelo menos uma mensagem para excluir!",
        "info"
      );
      return;
    }

    Swal.fire({
      title: "Tem certeza?",
      text: `Você está prestes a excluir ${selectedRows.length} mensagem(ns). Esta ação é irreversível.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`${AUTOMATIONS_URL}delete_messages/`, {
            data: { ids: ids },
          });
          setNotify({
            title: "Sucesso!",
            message: "Mensagens excluídas com sucesso!",
            type: "success",
            visible: true,
          });
          setSelectedRows([]);
          fetchMessageHistory();
        } catch (err) {
          setNotify({
            title: "Erro!",
            message: "Erro ao excluir as mensagens.",
            type: "error",
            visible: true,
          });
        }
      }
    });
  };

  const handleManualSend = (message) => {
    Swal.fire({
      title: "Enviar mensagem manualmente?",
      text: `Deseja enviar a "${message.title}" manualmente para ${message.contact_name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Sim, enviar!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.post(`${AUTOMATIONS_URL}manual_send/`, {
            message_id: message.id,
          });
          setNotify({
            title: "Sucesso!",
            message: "Mensagem enviada manualmente com sucesso!",
            type: "success",
            visible: true,
          });
          fetchMessageHistory();
        } catch (err) {
          setNotify({
            title: "Erro!",
            message: "Erro ao enviar mensagem manualmente.",
            type: "error",
            visible: true,
          });
        }
      }
    });
  };

  const handleViewDetails = (message) => {
    setSelectedMessage(message);
    setShowModal(true);
  };

  const columns = [
    {
      name: "Título",
      selector: (row) => row.title,
      sortable: true,
      width: "20%",
    },
    {
      name: "Telefone",
      selector: (row) => formatPhone(row.number),
      sortable: true,
      width: "15%",
    },
    {
      name: "Tipo",
      selector: (row) => getTemplateActionLabel(row.template_action),
      sortable: true,
      width: "10%",
    },
    {
      name: "Data Agendada",
      selector: (row) =>
        new Date(row.send_date).toLocaleString(),
      sortable: true,
      width: "15%",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => getStatusBadge(row),
      sortable: true,
      width: "10%",
    },
    {
      name: "Enviada em",
      selector: (row) => row.last_send,
      cell: (row) =>
        row.last_send ? (
          new Date(row.last_send).toLocaleString()
        ) : row.status === "failed" ? (
          <span className="text-danger">{row.error || "Erro no envio"}</span>
        ) : (
          "Pendente"
        ),
      sortable: true,
      width: "10%",
    },
    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            title="Ver Detalhes"
            onClick={() => handleViewDetails(row)}
          >
            <IconEye size={16} />
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            title="Enviar Manualmente"
            onClick={() => handleManualSend(row)}
          >
            <IconSend size={16} />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "15%",
    },
  ];

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Histórico de Mensagens</h5>
        {showDeleteButton && (
          <Button
            variant="danger"
            size="sm"
            onClick={handleBulkDelete}
            className="d-flex align-items-center"
          >
            <IconTrash size={18} className="me-1" />
            Excluir Selecionados
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="all">Todos</option>
                <option value="sent">Enviadas</option>
                <option value="pending">Pendentes</option>
                <option value="failed">Falhas</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Período</Form.Label>
              <Form.Select
                name="dateRange"
                value={filters.dateRange}
                onChange={handleFilterChange}
              >
                <option value="today">Hoje</option>
                <option value="yesterday">Ontem</option>
                <option value="7days">Últimos 7 dias</option>
                <option value="30days">Últimos 30 dias</option>
                <option value="all">Todo o período</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Buscar</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="text"
                  name="search"
                  placeholder="Nome ou número..."
                  value={filters.search}
                  onChange={handleFilterChange}
                />
                <Button variant="outline-secondary">
                  <IconSearch size={18} />
                </Button>
              </div>
            </Form.Group>
          </Col>

          <Col md={2} className="d-flex align-items-end">
            <Button
              variant="outline-primary"
              onClick={exportToCSV}
              className="w-100 d-flex align-items-center justify-content-center"
            >
              <IconFileDownload size={18} className="me-1" />
              Exportar
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando mensagens...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center p-4">
            <p className="mb-0">
              Nenhuma mensagem encontrada para os filtros selecionados.
            </p>
          </div>
        ) : (
          <DatatablePaginated
            componentName="message-history"
            data={messages}
            columns={columns}
            totalRows={totalRows}
            handlePageChange={handlePageChange}
            pagination={true}
            paginationServer={true}
            paginationTotalRows={totalRows}
            paginationPerPage={filters.page_size}
            paginationDefaultPage={filters.page}
            isLoading={loading}
            selectable={true}
            filterByField={false}
            onSelectedRowsChange={onSelectedRowsChange}
            classes="message-history-table"
          />
        )}
      </Card.Body>

      {/* Modal de Detalhes */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da Mensagem</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage && (
            <div>
              <h5>{selectedMessage.title}</h5>
              <p>
                <strong>Contato:</strong> {selectedMessage.contact_name}
              </p>
              <p>
                <strong>Telefone:</strong> {formatPhone(selectedMessage.number)}
              </p>
              <p>
                <strong>Tipo:</strong>{" "}
                {getTemplateActionLabel(selectedMessage.template_action)}
              </p>
              <p>
                <strong>Agendamento:</strong>{" "}
                {new Date(selectedMessage.send_date).toLocaleString()}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                {selectedMessage.last_send ? "Enviada" : "Pendente"}
              </p>

              {selectedMessage.last_send && (
                <p>
                  <strong>Enviada em:</strong>{" "}
                  {new Date(selectedMessage.last_send).toLocaleString()}
                </p>
              )}

              <h6 className="mt-4">Conteúdo da Mensagem</h6>
              <div className="border p-3 rounded bg-light">
                {selectedMessage.message.type === "document" ? (
                  <div>
                    <p>
                      <strong>Tipo de Conteúdo:</strong> Documento
                    </p>
                    <p>
                      <strong>Mensagem:</strong>{" "}
                      {selectedMessage.message.message}
                    </p>
                    <p>
                      <strong>Documento:</strong>{" "}
                      {selectedMessage.message.document_name}
                    </p>
                    <p>
                      <a
                        href={selectedMessage.message.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visualizar Documento
                      </a>
                    </p>
                  </div>
                ) : (
                  <p>
                    {selectedMessage.message.message ||
                      JSON.stringify(selectedMessage.message)}
                  </p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Fechar
          </Button>
          {selectedMessage && !selectedMessage.last_send && (
            <Button
              variant="success"
              onClick={() => {
                setShowModal(false);
                handleManualSend(selectedMessage);
              }}
            >
              <IconSend size={18} className="me-1" />
              Enviar Manualmente
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default MessageHistory;
